import * as Yup from 'yup';
import { useState, useRef } from 'react';
import { Icon } from '@iconify/react';
import { useFormik, Form, FormikProvider } from 'formik';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import { useNavigate } from 'react-router-dom';
// material
import {
  Stack,
  TextField,
  IconButton,
  InputAdornment,
  Dialog,
  DialogContent,
  MenuItem
} from '@mui/material';
import { LoadingButton, Alert } from '@mui/lab';
import { useAuth } from '../../../context/AuthContext';

// ----------------------------------------------------------------------

export default function RegisterForm() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successDialog, setSuccessDialog] = useState(false);
  const [dialogText, setDialogText] = useState('');

  const emailRef = useRef();
  const passwordRef = useRef();
  const userTypeRef = useRef();
  const firstNameRef = useRef();
  const lastNameRef = useRef();
  const { register } = useAuth();

  async function handleFormSubmit(e) {
    e.preventDefault();
    const userName = `${firstNameRef.current.value} ${lastNameRef.current.value}`;
    try {
      setLoading(true);
      await register(
        emailRef.current.value,
        passwordRef.current.value,
        userName,
        userTypeRef.current.value
      );
      setLoading(false);
      setSuccessDialog(true);
      setDialogText('Account created successfully');
      <Alert severity="success">This is a success alert — check it out!</Alert>;
      setTimeout(() => {
        setSuccessDialog(false);
        setDialogText('');
        navigate('/login', { replace: true });
      }, 2000);
    } catch (error) {
      setLoading(false);
      setSuccessDialog(true);
      setDialogText('Something went wrong');
      setTimeout(() => {
        setSuccessDialog(false);
        setDialogText('');
      }, 2000);
    }
  }

  const RegisterSchema = Yup.object().shape({
    firstName: Yup.string()
      .min(2, 'Too Short!')
      .max(50, 'Too Long!')
      .required('First name required'),
    lastName: Yup.string().min(2, 'Too Short!').max(50, 'Too Long!').required('Last name required'),
    email: Yup.string().email('Email must be a valid email address').required('Email is required'),
    password: Yup.string().min(6).required('Password is required'),
    userType: Yup.string().required('Please select your account type')
  });

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      userType: ''
    },
    validationSchema: RegisterSchema,
    onSubmit: () => {
      navigate('/dashboard', { replace: true });
    }
  });

  const { errors, touched, getFieldProps } = formik;

  const userTypes = [
    { label: 'Student', value: 'student' },
    { label: 'Teacher', value: 'teacher' },
    { label: 'Admin', value: 'admin' }
  ];

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={(e) => handleFormSubmit(e)}>
        <Stack spacing={3}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label="First name"
              inputRef={firstNameRef}
              {...getFieldProps('firstName')}
              error={Boolean(touched.firstName && errors.firstName)}
              helperText={touched.firstName && errors.firstName}
            />

            <TextField
              fullWidth
              label="Last name"
              inputRef={lastNameRef}
              {...getFieldProps('lastName')}
              error={Boolean(touched.lastName && errors.lastName)}
              helperText={touched.lastName && errors.lastName}
            />
          </Stack>

          <TextField
            fullWidth
            autoComplete="username"
            type="email"
            label="Email address"
            inputRef={emailRef}
            {...getFieldProps('email')}
            error={Boolean(touched.email && errors.email)}
            helperText={touched.email && errors.email}
          />

          <TextField
            select
            margin="dense"
            label="User type"
            inputRef={userTypeRef}
            {...getFieldProps('userType')}
            fullWidth
            // onChange={(e) => setUserType(e.target.value)}
            error={Boolean(touched.userType && errors.userType)}
            helperText={touched.userType && errors.userType}
          >
            {userTypes.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            autoComplete="current-password"
            inputRef={passwordRef}
            type={showPassword ? 'text' : 'password'}
            label="Password"
            {...getFieldProps('password')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end" onClick={() => setShowPassword((prev) => !prev)}>
                    <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                  </IconButton>
                </InputAdornment>
              )
            }}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
          />
          {emailRef.current?.value && passwordRef.current?.value && userTypeRef.current?.value ? (
            <LoadingButton
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              loading={loading}
            >
              Register
            </LoadingButton>
          ) : (
            <LoadingButton fullWidth size="large" type="submit" variant="contained" disabled>
              {' '}
              Register
            </LoadingButton>
          )}
        </Stack>
      </Form>
      <Dialog open={successDialog} aria-labelledby="draggable-dialog-title">
        <DialogContent>{dialogText}</DialogContent>
      </Dialog>
    </FormikProvider>
  );
}
